import {getAuthenticationToken} from 'App/features/user/userApi';

import getConfig from './load_config';
import type {BuildrzConfig} from './load_config';

type Method = 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH';

type GetOptions<T> = {
  queryParameters?: {[key: string]: string};
  body?: {[key: string]: unknown};
  callback?: () => T;
  finally?: () => T;
  signal?: AbortSignal | null;
};
type Options<T> = GetOptions<T> & {
  id?: string;
};

const HTTP_STATUS = {
  NO_CONTENT: 204,
};

export default class BaseRequest<T> {
  private apiUrl: string;
  private authorization?: string;

  private config: BuildrzConfig;
  private controller: AbortController | null;

  constructor(resourceName: string, authorization?: string) {
    this.config = getConfig();
    this.controller = null;
    this.apiUrl = `${this.config.apiOrigin}/${resourceName}/`;
    this.authorization = authorization;
  }

  handleErrors = (response: Response): Response => {
    if (!response.ok) {
      throw response;
    }
    return response;
  };

  request = (method: Method, options: Options<T> = {}): Promise<T> => {
    let url = this.apiUrl;
    if (options.id) {
      url += `${options.id}/`;
    }

    if (options.queryParameters) {
      const params = Object.entries(options.queryParameters)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      url += `?${params}`;
    }

    const initInformation: RequestInit = {
      method,
      headers: {
        Accept: 'application/json',
        Authorization: this.authorization || getAuthenticationToken(),
        'Content-Type': 'application/json',
      },
    };

    if (options.body) {
      initInformation.body = JSON.stringify(options.body);
    }

    return fetch(url, {...initInformation, signal: options.signal})
      .then(this.handleErrors)
      .then(response =>
        response.status !== HTTP_STATUS.NO_CONTENT
          ? response.json()
          : undefined,
      )
      .then(options.callback)
      .finally(options.finally);
  };

  get = (id: string, options: GetOptions<T> = {}): Promise<T> => {
    return this.request('GET', {id, ...options});
  };

  getAll = (options: GetOptions<T> = {}): Promise<T> => {
    return this.request('GET', {...options});
  };

  post = (item: {[key: string]: unknown}, callback?: () => T): Promise<T> => {
    return this.request('POST', {body: item, callback});
  };

  put = (
    id: string,
    itemToSave: {[key: string]: string},
    callback?: () => T,
  ): Promise<T> => {
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();
    return this.request('PUT', {
      id,
      body: itemToSave,
      callback,
      signal: this.controller?.signal,
    });
  };

  delete = (id: string, callback?: () => T): Promise<T> => {
    return this.request('DELETE', {id, callback});
  };

  deleteBy = (
    item: {[key: string]: string},
    callback?: () => T,
  ): Promise<T> => {
    return this.request('DELETE', {body: item, callback});
  };

  patch = (
    id: string,
    item: {[key: string]: unknown},
    callback?: () => T,
  ): Promise<T> => {
    return this.request('PATCH', {id, body: item, callback});
  };
}
